import { useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Modal } from "../../../components/core/basic/Modal";
import { Button } from "../../../components/core/basic/Button";
import { ProductSelector } from "../../../components/core/blocks/ProductSelector/ProductSelector";
import { DatesProvider } from "../../../dates/DatesContext";
import { useBulkUpdateAdPropertiesV2 } from "../api/useBulkUpdateAdPropertiesV2";
import { AD_TYPES } from "../../../components/core/blocks/AdPlan/constants";
import { BulkAdUpdateFeedbackView, type BulkAdUpdateGroupConfig } from "./BulkAdUpdateFeedbackView";
import type { TFunction } from "i18next";

interface BulkAddProductsToAdsModalProps {
  advertisementIds: number[];
  renderAdEligibilityWarning: boolean;
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const buildAddProductsGroupConfig = (t: TFunction<any, any>): BulkAdUpdateGroupConfig => ({
  NTM_BID_ACCEPTANCE_REQUIRED: { title: "Not bid-managed by Trellis" },
  UNSUPPORTED_AD_TYPE: {
    title: "Unsupported ad type",
    getDetail: (error) => {
      const value = typeof error.details?.ad_type === "number" ? error.details.ad_type : undefined;
      const sel = Object.values(AD_TYPES).find((entry) => entry.value === value)?.label;
      return sel ? t(sel) : `Ad type ${value ?? "unknown"}`;
    },
  },
});

export const BulkAddProductsToAdsModal = NiceModal.create(({ advertisementIds }: BulkAddProductsToAdsModalProps) => {
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const { t } = useTranslation(["advertising"]);
  const groupConfig = buildAddProductsGroupConfig(t);

  const modal = useModal();
  const bulkUpdatePropertiesMutation = useBulkUpdateAdPropertiesV2();

  const isPlural = advertisementIds.length !== 1;

  const handleConfirm = () => {
    const productIds = selectedProductIds.map((id) => parseInt(id)).filter((n) => !Number.isNaN(n));

    bulkUpdatePropertiesMutation.mutate(
      {
        advertisements: advertisementIds,
        updates: [{ field: "products", action: "add", value: productIds }],
      },
      {
        onSuccess: (data) => {
          if (data.every((r) => !r.error)) {
            modal.remove();
          }
        },
      },
    );
  };

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => !isOpen && modal.remove()}
      title={`Add Products to Media Plan${isPlural ? "s" : ""}`}
      css={{ width: "55vw", padding: "large", overflowY: "auto" }}
    >
      <DatesProvider>
        <VStack
          className="custom-scroll-container"
          css={{
            gap: "large",
            alignItems: "stretch",
            paddingY: "xlarge",
            paddingX: "xlarge",
            minHeight: "0",
            flex: "1",
          }}
        >
          {bulkUpdatePropertiesMutation.isSuccess && bulkUpdatePropertiesMutation.data.some((r) => r.error) ?
            <BulkAdUpdateFeedbackView
              results={bulkUpdatePropertiesMutation.data}
              groupConfig={groupConfig}
              onDone={() => modal.remove()}
            />
          : <SelectionView
              advertisementCount={advertisementIds.length}
              selectedProductIds={selectedProductIds}
              onSelectionChange={setSelectedProductIds}
              onCancel={() => modal.remove()}
              onConfirm={handleConfirm}
              isPending={bulkUpdatePropertiesMutation.isPending}
            />
          }
        </VStack>
      </DatesProvider>
    </Modal>
  );
});

interface SelectionViewProps {
  advertisementCount: number;
  selectedProductIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

const SelectionView = ({
  advertisementCount,
  selectedProductIds,
  onSelectionChange,
  onCancel,
  onConfirm,
  isPending,
}: SelectionViewProps) => {
  const { t } = useTranslation(["advertising"]);
  return (
    <>
      <p className={css({ color: "gray.600", fontSize: "medium", textAlign: "center" })}>
        {t(
          ($) =>
            $.advertising[
              "Products will be added to all selected Sponsored Product and Sponsored Display media plans that are managed by Trellis. Sponsored Brand media plans and media plans not bid-managed by Trellis will be skipped."
            ],
          { count: advertisementCount },
        )}
      </p>
      <VStack className={cx("custom-scroll-container", css({ overflow: "auto", paddingY: "xlarge" }))}>
        <ProductSelector
          context="bulkAddProductsToAds"
          rowIdAccessor="product_id"
          enableMultiRowSelection
          enableSelectAll
          selectedRowIds={selectedProductIds}
          onSelectionChange={onSelectionChange}
        />
      </VStack>
      <HStack css={{ justifyContent: "center", gap: "xlarge" }}>
        <Button variant="secondary" onPress={onCancel}>
          {t(($) => $.advertising.Cancel)}
        </Button>
        <Button onPress={onConfirm} isPending={isPending} isDisabled={selectedProductIds.length === 0}>
          {t(($) => $.advertising["Add Products"])}
        </Button>
      </HStack>
    </>
  );
};
